/**
 * How many metric log rows a time range may select. Snap includes the day
 * that reaches this count so the range sits at the limit (bar turns red).
 */
export const METRICS_CHART_BUCKET_LIMIT = 10_000;

const startOfDayTime = (date: string | Date | number) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** Unique start-of-day times for logs, oldest first. */
export const logDayTimesFromLogs = (
  logs: { date: string }[] | null | undefined,
): number[] => {
  if (!logs?.length) return [];
  const days = new Set<number>();
  logs.forEach((log) => {
    const t = Date.parse(log.date);
    if (!Number.isNaN(t)) days.add(startOfDayTime(t));
  });
  return Array.from(days).sort((a, b) => a - b);
};

/** True when this pool is at or over `METRICS_CHART_BUCKET_LIMIT`. */
export const isAtMetricsChartLogLimit = (
  logs: { date: string }[] | null | undefined,
): boolean => (logs?.length || 0) >= METRICS_CHART_BUCKET_LIMIT;

const endOfDayTime = (dayTime: number) => {
  const d = new Date(dayTime);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};

/**
 * Pull the side opposite `anchor` inward to the day that reaches
 * `METRICS_CHART_BUCKET_LIMIT`. That day is included in full (range stays red).
 * A single day with more than the limit is kept as that day.
 */
export const snapRangeToChartLogLimit = (
  logs: { date: string }[] | null | undefined,
  startT: number,
  endT: number,
  anchor: 'start' | 'end',
): { startT: number; endT: number } => {
  const s = startOfDayTime(startT);
  const e = startOfDayTime(endT);
  let startDay = s;
  let endDay = e;
  if (startDay > endDay) {
    if (anchor === 'end') startDay = endDay;
    else endDay = startDay;
  }
  if (!logs?.length) return { startT: startDay, endT: endDay };
  const rangeStartMs = startDay;
  const rangeEndMs = endOfDayTime(endDay);
  let inRange = 0;
  logs.forEach((log) => {
    const t = Date.parse(log.date);
    if (!Number.isNaN(t) && t >= rangeStartMs && t <= rangeEndMs) inRange += 1;
  });
  if (inRange <= METRICS_CHART_BUCKET_LIMIT) return { startT: startDay, endT: endDay };

  const dayCounts = new Map<number, number>();
  const poolEndMs = anchor === 'end' ? rangeEndMs : Number.POSITIVE_INFINITY;
  const poolStartMs = anchor === 'start' ? rangeStartMs : Number.NEGATIVE_INFINITY;
  logs.forEach((log) => {
    const t = Date.parse(log.date);
    if (Number.isNaN(t) || t < poolStartMs || t > poolEndMs) return;
    const day = startOfDayTime(t);
    dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });
  const days = Array.from(dayCounts.keys()).sort((a, b) => a - b);
  if (!days.length) return { startT: startDay, endT: endDay };

  if (anchor === 'end') {
    let total = 0;
    let newStart = endDay;
    for (let i = days.length - 1; i >= 0; i -= 1) {
      const day = days[i];
      if (day > endDay) continue;
      const n = dayCounts.get(day) || 0;
      total += n;
      newStart = day;
      if (total >= METRICS_CHART_BUCKET_LIMIT) break;
    }
    return { startT: newStart, endT: endDay };
  }
  let total = 0;
  let newEnd = startDay;
  for (let i = 0; i < days.length; i += 1) {
    const day = days[i];
    if (day < startDay) continue;
    const n = dayCounts.get(day) || 0;
    total += n;
    newEnd = day;
    if (total >= METRICS_CHART_BUCKET_LIMIT) break;
  }
  return { startT: startDay, endT: newEnd };
};

/** Start of the oldest day kept after taking the newest `METRICS_CHART_BUCKET_LIMIT` rows. */
export const oldestKeptChartLogDayTime = (
  logs: { date: string }[] | null | undefined,
): number => {
  if (!logs?.length) return 0;
  if (logs.length <= METRICS_CHART_BUCKET_LIMIT) {
    let oldest = Number.POSITIVE_INFINITY;
    logs.forEach((log) => {
      const t = Date.parse(log.date);
      if (!Number.isNaN(t) && t < oldest) oldest = t;
    });
    return Number.isFinite(oldest) ? startOfDayTime(oldest) : 0;
  }
  const newestFirst = [...logs].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  const cutoff = newestFirst[METRICS_CHART_BUCKET_LIMIT - 1];
  return cutoff ? startOfDayTime(cutoff.date) : 0;
};

/** Keep logs on/after the limit day so a day is never cut in half. */
export const windowLogsToChartLimit = <T extends { date: string }>(
  logs: T[] | null | undefined,
): T[] => {
  if (!logs?.length) return [];
  const oldestDay = oldestKeptChartLogDayTime(logs);
  if (!oldestDay) return logs;
  return logs.filter((log) => Date.parse(log.date) >= oldestDay);
};

const MS_PER_DAY = 60 * 60 * 24 * 1000;
// Both-move next start is the day after the old end, and a week is 7 elapsed
// days, so each slot is 8 calendar days (start 0, 8, 16, …). Dividing by 7
// skipped a number: those starts are floor(n/7) = 0,1,2,3,4,5,6,8 (week 8 gone).
const WEEK_SLOT_DAYS = 8;

/**
 * Week slot from the first log day (0 = that day through +7 inclusive).
 */
const weekBucketFromFirstLogDay = (dayTime: number, firstDayTime: number) => {
  const days = Math.round(
    (startOfDayTime(dayTime) - startOfDayTime(firstDayTime)) / MS_PER_DAY,
  );
  return Math.floor(Math.max(0, days) / WEEK_SLOT_DAYS);
};

/** Ordered unique week / month / year keys for the log-day window. */
export const metricsRangeLevelKeys = (
  logDayTimes: number[],
  moveType: 'day' | 'week' | 'month' | 'year',
): number[] => {
  if (!logDayTimes.length) return [];
  const sorted = [...logDayTimes].sort((a, b) => a - b);
  if (moveType === 'day') return sorted;
  const firstDayTime = sorted[0];
  const keys = new Set<number>();
  sorted.forEach((dayTime) => {
    const d = new Date(dayTime);
    if (moveType === 'week') {
      keys.add(weekBucketFromFirstLogDay(dayTime, firstDayTime));
      return;
    }
    if (moveType === 'year') {
      keys.add(d.getFullYear());
      return;
    }
    keys.add(d.getFullYear() * 12 + d.getMonth());
  });
  return Array.from(keys).sort((a, b) => a - b);
};

export const countMetricsRangeLevels = (
  logDayTimes: number[],
  moveType: 'day' | 'week' | 'month' | 'year',
): number => Math.max(1, metricsRangeLevelKeys(logDayTimes, moveType).length);

const levelKeyForTime = (
  time: number,
  moveType: 'day' | 'week' | 'month' | 'year',
  firstDayTime: number,
): number => {
  const d = new Date(time);
  d.setHours(0, 0, 0, 0);
  if (moveType === 'day') return d.getTime();
  if (moveType === 'week') return weekBucketFromFirstLogDay(d.getTime(), firstDayTime);
  if (moveType === 'year') return d.getFullYear();
  return d.getFullYear() * 12 + d.getMonth();
};

/**
 * 1-based level of a date in the limited window. Leftmost log day is 1;
 * the last log day is N (e.g. day 333).
 */
export const metricsRangeLevelAtTime = (
  logDayTimes: number[],
  moveType: 'day' | 'week' | 'month' | 'year',
  time: number,
): number => {
  const keys = metricsRangeLevelKeys(logDayTimes, moveType);
  if (!keys.length) return 1;
  const firstDayTime = logDayTimes.reduce((min, t) => (t < min ? t : min));
  const target = levelKeyForTime(time, moveType, firstDayTime);
  let level = 1;
  for (let i = 0; i < keys.length; i += 1) {
    if (keys[i] <= target) level = i + 1;
    else break;
  }
  return level;
};

export const formatMetricsRangeLevelLabel = (
  moveType: 'day' | 'week' | 'month' | 'year',
  level: number,
): string => `${moveType} ${level}`;
